// src/pages/CommonPartsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import CommonPartsList from '../components/CommonPartsList';
import CommonPartEditor from '../components/CommonPartEditor';
import {
    getCommonParts,
    createCommonPart,
    updateCommonPart,
    deleteCommonPart
} from '../services/commonPartsService';
import { PART_TYPES } from '../types/commonParts';
import '../styles/pages/CommonPartsPage.scss';

const CommonPartsPage = () => {
    const { accountId } = useParams();
    const { currentUser } = useAuth();

    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('list');
    const [editingPart, setEditingPart] = useState(null);
    const [filterType, setFilterType] = useState('all');

    // 共通パーツ一覧を取得
    const fetchParts = async () => {
        if (!currentUser) return;

        try {
            setLoading(true);
            setError('');

            const fetchedParts = await getCommonParts(accountId);
            setParts(fetchedParts);
        } catch (err) {
            console.error('共通パーツの取得に失敗:', err);
            setError('共通パーツの取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParts();
    }, [currentUser, accountId]);

    // 新規パーツ作成
    const handleCreatePart = async (partData) => {
        try {
            const newPart = await createCommonPart(accountId, partData);
            setParts(prev => [newPart, ...prev]);
            setActiveTab('list');
            return newPart;
        } catch (err) {
            console.error('パーツの作成に失敗:', err);
            throw err;
        }
    };

    // パーツ更新
    const handleUpdatePart = async (partId, updateData) => {
        try {
            const updatedPart = await updateCommonPart(accountId, partId, updateData);
            setParts(prev => prev.map(part =>
                part.id === partId ? updatedPart : part
            ));
            setEditingPart(null);
            setActiveTab('list');
            return updatedPart;
        } catch (err) {
            console.error('パーツの更新に失敗:', err);
            throw err;
        }
    };

    // パーツ削除
    const handleDeletePart = async (partId) => {
        if (!window.confirm('このパーツを削除しますか？')) return;

        try {
            await deleteCommonPart(accountId, partId);
            setParts(prev => prev.filter(part => part.id !== partId));
        } catch (err) {
            console.error('パーツの削除に失敗:', err);
            setError('パーツの削除に失敗しました。');
        }
    };

    // パーツ編集開始
    const handleEditPart = (part) => {
        setEditingPart(part);
        setActiveTab('edit');
    };

    // フィルタリングされたパーツ一覧
    const filteredParts = parts.filter(part => {
        const typeMatch = filterType === 'all' || part.type === filterType;
        return typeMatch;
    });

    if (loading) {
        return (
            <div>
                <Header />
                <main className="common-parts-page">
                    <div className="container">
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            <p>読み込み中...</p>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div>
            <Header />
            <main className="common-parts-page">
                <div className="container">

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {activeTab === 'list' && (
                        <div className="parts-list-header">
                            <button
                                onClick={() => {
                                    setEditingPart(null);
                                    setActiveTab('create');
                                }}
                                className="create-part-button"
                            >
                                新規パーツ作成
                            </button>
                        </div>
                    )}

                    {activeTab === 'create' && (
                        <div className="editor-header">
                            <button
                                onClick={() => setActiveTab('list')}
                                className="back-to-list-button"
                            >
                                ← 一覧に戻る
                            </button>
                        </div>
                    )}

                    {editingPart && activeTab === 'edit' && (
                        <div className="editor-header">
                            <button
                                onClick={() => {
                                    setEditingPart(null);
                                    setActiveTab('list');
                                }}
                                className="back-to-list-button"
                            >
                                ← 一覧に戻る
                            </button>
                        </div>
                    )}

                    <div className="tab-content">
                        {activeTab === 'list' && (
                            <CommonPartsList
                                parts={filteredParts}
                                filterType={filterType}
                                onFilterTypeChange={setFilterType}
                                onEditPart={handleEditPart}
                                onDeletePart={handleDeletePart}
                            />
                        )}

                        {(activeTab === 'create' || activeTab === 'edit') && (
                            <CommonPartEditor
                                part={editingPart}
                                onSave={editingPart ?
                                    (formData) => handleUpdatePart(editingPart.id, formData) :
                                    handleCreatePart
                                }
                                onCancel={() => {
                                    setEditingPart(null);
                                    setActiveTab('list');
                                }}
                            />
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CommonPartsPage;
